import { Group, Stack } from '@mantine/core';
import { secondaryBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import type { ExtendedComboboxItem } from '../../../../ui-library/select/select.type';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getTemplateObjectType } from './dashboard-template-helpers';
import type { ObjectKeyInfo } from './dashboard-template.type';

type DashboardTemplateMappingKeyInputProps = {
  keyInfo: ObjectKeyInfo;
  options: ExtendedComboboxItem[];
  value: string | null;
  onChange: (value: string) => void;
};

/**
 * One row in the template's "Mapping" panel — a tooltipped object-type label
 * and a searchable Select bound to a single `objectKey` -> `uuid` mapping.
 * The tooltip lists the components that reference this key so users know
 * what the dropdown affects.
 *
 * @param {DashboardTemplateMappingKeyInputProps} props - The key being mapped, dropdown options, current value, and change callback
 * @returns {JSX.Element} The label + Select pair
 */
export const DashboardTemplateMappingKeyInput = ({
  keyInfo,
  options,
  value,
  onChange,
}: DashboardTemplateMappingKeyInputProps) => {
  const objectType = getTemplateObjectType(keyInfo.objectType);

  return (
    <Stack gap={5}>
      <Tooltip
        label={
          <Stack gap={5}>
            <Text size="regular" weight="medium">
              {`${objectType} used in:`}
            </Text>
            {keyInfo.usedInComponents.map((component) => (
              <Text size="small" key={component}>
                {component}
              </Text>
            ))}
          </Stack>
        }
        variant="black"
        multiline
        style={{ maxWidth: 400 }}
      >
        <Group gap="xs">
          <Text size="small" weight="medium">
            {objectType}
          </Text>
          <Icon name="info" size={16} color={secondaryBase} />
        </Group>
      </Tooltip>
      <Select
        placeholder={`Select ${objectType}...`}
        options={options}
        value={value}
        onChange={(next) => next && onChange(next)}
        searchable
        style={{ width: '250px' }}
      />
    </Stack>
  );
};
